/**
 * src/components/AdminRoute.js
 * 
 * Route guard for admin-only pages. 
 * Checks authentication + admin role from Firestore.
 * Shows loading spinner while checking, redirects non-admins to /farm.
 * 
 * Usage:
 *   <AdminRoute><AdminDashboard /></AdminRoute>
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdmin } from "../hooks/useAdmin";
import { Shield, ShieldOff } from "lucide-react";

function AdminRoute({ children }) {
  const location = useLocation();
  const { user, isAdmin, loading } = useAdmin();

  // Loading state — minimal spinner
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
            <Shield className="w-6 h-6 text-green-600 dark:text-green-400 animate-pulse" />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Verifying access…</p>
        </div>
      </div>
    );
  }

  // Not authenticated — redirect to login
  if (!user) {
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }

  // Authenticated but not admin — access denied
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-neutral-950 px-4">
        <div className="w-full max-w-md bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-6 sm:p-8 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
            <ShieldOff className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            You don't have administrator privileges. Contact a system admin if you believe this is an error.
          </p>
          <button
            onClick={() => (window.location.href = "/farm")}
            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
          >
            Return to Farm
          </button>
        </div>
      </div>
    );
  }

  // All checks passed
  return children;
}

export default AdminRoute;
